/**
 * Talks to the relay's password-reset endpoints (`app/src/auth/routes.ts`).
 *
 * Mirrors contact-schema.ts's shape — a zod schema plus a small fetch wrapper —
 * without sharing code with it: the relay and the contact Pages Function are
 * different backends with different response shapes, and coupling them would
 * make an unrelated change to one a reason to touch the other.
 */
import { z } from 'zod';

/**
 * Mirrors MIN_PASSWORD_LENGTH in app/src/auth/routes.ts as a courtesy — a
 * shorter password never reaches the network. The server is still the
 * authority; this only saves a round trip on the common case.
 */
export const MIN_PASSWORD_LENGTH = 10;

export const RELAY_ORIGIN =
  (import.meta.env.VITE_RELAY_ORIGIN as string | undefined) || 'https://relay.brambleapp.xyz';

export const forgotSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, { message: 'Please enter your email address.' })
    .email({ message: 'That does not look like an email address.' }),
});
export type ForgotFormValues = z.infer<typeof forgotSchema>;

export const resetSchema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, { message: `Please enter at least ${MIN_PASSWORD_LENGTH} characters.` }),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    message: 'Passwords do not match.',
    path: ['confirm'],
  });
export type ResetFormValues = z.infer<typeof resetSchema>;

/**
 * The code step: email plus the typed code plus a new password. `code` is
 * deliberately not shape-validated here — the server strips anything outside
 * its own alphabet and uppercases before comparing (`app/src/auth/reset.ts`),
 * so a hyphen, spaces, or lowercase from a pasted code are all fine. Requiring
 * a particular shape client-side would just be a second place for that rule to
 * drift out of sync with the server's.
 */
export const codeResetSchema = z
  .object({
    email: z
      .string()
      .trim()
      .min(1, { message: 'Please enter your email address.' })
      .email({ message: 'That does not look like an email address.' }),
    code: z.string().trim().min(1, { message: 'Please enter your code.' }),
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, { message: `Please enter at least ${MIN_PASSWORD_LENGTH} characters.` }),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    message: 'Passwords do not match.',
    path: ['confirm'],
  });
export type CodeResetFormValues = z.infer<typeof codeResetSchema>;

/**
 * Request a reset link.
 *
 * Always resolves. `/forgot` answers the same shape whether or not the
 * address is registered, and the page must never distinguish either — so this
 * deliberately discards the response (and swallows a network failure the same
 * way) rather than giving the caller anything to branch on.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  try {
    await fetch(`${RELAY_ORIGIN}/v1/auth/forgot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    // A network failure looks the same as any other outcome here — see above.
  }
}

export class ResetSubmitError extends Error {}

/**
 * Shared by both ways of redeeming a reset — a body is either `{ token,
 * newPassword }` or `{ email, code, newPassword }`, never both
 * (`app/src/auth/routes.ts`). `invalidMessage` is the only thing that differs:
 * a link and a code fail for a caller in different-sounding ways ("that link"
 * vs. "that code"), but the server collapses every failure — unknown email,
 * wrong code, expired, five attempts burned, already used — onto the same
 * `invalid_token`, so this must not try to tell them apart either.
 */
async function postReset(body: Record<string, unknown>, invalidMessage: string): Promise<void> {
  const res = await fetch(`${RELAY_ORIGIN}/v1/auth/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  let parsed: { ok?: boolean; error?: string } | null = null;
  try {
    parsed = (await res.json()) as { ok?: boolean; error?: string };
  } catch {
    parsed = null;
  }

  if (!res.ok || !parsed?.ok) {
    if (parsed?.error === 'weak_password') {
      throw new ResetSubmitError(`Please use at least ${MIN_PASSWORD_LENGTH} characters.`);
    }
    throw new ResetSubmitError(invalidMessage);
  }
}

/**
 * Redeem a reset link. Unlike `requestPasswordReset`, this one does branch —
 * the token already proves the caller read the email, so there is no address
 * left to protect by staying silent about the result.
 */
export async function submitPasswordReset(token: string, newPassword: string): Promise<void> {
  return postReset({ token, newPassword }, 'That link is invalid or has expired.');
}

/**
 * Redeem a reset code — the fallback for a parent who has the email open but
 * not the link, e.g. reading it on the same device as the app. Same
 * indistinguishability rule as the link: a wrong code and an unknown email
 * must produce the exact same message, so this never surfaces the server's
 * `invalid_token` as anything more specific.
 */
export async function submitPasswordResetCode(email: string, code: string, newPassword: string): Promise<void> {
  return postReset({ email, code, newPassword }, 'That code is invalid or has expired.');
}
